import styled from '@emotion/styled';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button, pxToRem, StarRatingPicker } from '@modernhealth/vital-components';
import { CoachNode } from 'src/generated/webapp_gql';
import { ProviderType } from 'src/modules/user/models/user';
import { color, font } from 'src/shared/styles/variables';

import { getSessionUtilizationInfo } from '../selectors/videoSessionsSelectors';
import { RateSessionProviderAvatar } from './RateSessionProviderAvatar';

const HeaderText = styled.h4(
    {
        color: color.ebonyClay,
        marginTop: 20,
        marginBottom: 0,
        textAlign: 'center',
    },
    font.black(font.size.extraLarge),
);

const DescriptionText = styled.p(
    {
        color: color.blueBayoux,
        marginTop: 10,
        marginBottom: 15,
        textAlign: 'center',
    },
    font.book(font.size.large),
);

const DescriptionTextBold = styled.span(
    {
        color: color.blueBayoux,
    },
    font.heavy(font.size.large),
);

interface Props {
    onSkipClick: () => void;
    onRatingChange: (value: number) => void;
    provider: Pick<CoachNode, 'providerType' | 'firstName' | 'lastName' | 'avatar'>;
    rating?: number;
}

export const RateSessionIntroStep: React.FC<Props> = ({
    onSkipClick,
    provider,
    rating,
    onRatingChange,
}) => {
    const { t } = useTranslation();
    const {
        coveredTherapyVisits,
        coachingVisitsRemaining,
        coveredCoachingVisits,
        therapyVisitsRemaining,
    } = useSelector(getSessionUtilizationInfo);

    const isTherapySession = provider.providerType === ProviderType.THERAPIST;
    const coveredVisits = isTherapySession ? coveredTherapyVisits : coveredCoachingVisits;
    const remainingVisits = isTherapySession ? therapyVisitsRemaining : coachingVisitsRemaining;
    const isFirstSession = coveredVisits === remainingVisits;

    const isPsychiatrySession = provider.providerType === ProviderType.PSYCHIATRIST;

    const headerText = isPsychiatrySession
        ? t(`videoSessions:rate_session.intro.header.psychiatry`, {
              name: `${provider.firstName} ${provider.lastName}`,
          })
        : t(
              `videoSessions:rate_session.intro.header.${
                  isTherapySession ? 'therapy' : 'coaching'
              }${isFirstSession ? '.first' : ''}`,
              { name: provider.firstName },
          );

    return (
        <React.Fragment>
            <RateSessionProviderAvatar provider={provider} />
            <HeaderText>{headerText}</HeaderText>
            <DescriptionText>
                <Trans
                    t={t}
                    ns="videoSessions"
                    i18nKey={
                        isPsychiatrySession
                            ? 'rate_session.intro.description.pamm'
                            : `rate_session.intro.description.${
                                  isTherapySession ? 'therapist' : 'coach'
                              }`
                    }
                    components={{ highlight: <DescriptionTextBold /> }}
                />
            </DescriptionText>
            <StarRatingPicker defaultRating={rating} onRatingChange={onRatingChange} />

            <Button onClick={onSkipClick} style={{ marginTop: pxToRem(30) }} fluid>
                {t('common.skip')}
            </Button>
        </React.Fragment>
    );
};
